<template>
  <div class="container-sm background-white">
    <b-alert v-if="finishSuccess"
      :show="3"
      dismissible
      @dismissed="resetFinishSuccess()"
      fade
      variant="success"
    >
      Assessment successfully finished.
    </b-alert>
    <b-alert v-if="uploadSuccess"
      :show="3"
      dismissible
      @dismissed="resetUploadSuccess()"
      fade
      variant="success"
    >
      Assessment successfully uploaded.
    </b-alert>
    <b-alert v-if="uploading"
      show
      variant="warning"
    >
      <b-spinner small label="Uploading..."></b-spinner> Uploading assessment...
    </b-alert>
    <template>
      <div class="row align-items-center margin-right-auto">
        <div class="col-sm-6">
          <div class="home-title text-sm-left text-center">
            Communities Needing Assessment
            <span v-if="!online" class="you-are-offline">(You are offline)</span>
          </div>
        </div>
        <div class="col-sm-6 text-sm-right text-center">
          <b-button
            variant='success'
            size='sm'
            class='align-middle add-margin-5px'
            aria-label="Refresh"
            @click="getStuffFromServer()">
              Refresh
          </b-button>
          <b-button
            variant='success'
            size='sm'
            class='align-middle add-margin-5px'
            aria-label="Ad Hoc Community"
            @click="createAdHocCommunity()">
              Ad Hoc Community
          </b-button>
          <b-button
            variant='success'
            size='sm'
            class='align-middle add-margin-5px'
            aria-label="About This Tool"
            @click="showAboutModal()">
              About This Tool
          </b-button>
        </div>
      </div>
      <template v-if="loading">
        <div class="text-center">
          <b-spinner label="Loading" class="large-spinner"></b-spinner>
        </div>
      </template>
      <template v-else>
        <template v-if="availableCommunities.length === 0">
          <div class="no-comunities-text">
            {{ showTextWhenNoAvailableCommunities() }}
          </div>
        </template>
        <template v-else>
        <!-- Communities Needing Assessment for large devices -->
          <div class='d-none d-lg-block'>
          <b-table
            id="my-project-list"
            stacked='lg'
            :fields='fields'
            :items='availableCommunities'
            head-variant='dark'
            primary-key='data.item.CommunityId'
            :tbody-transition-props="transProps"
          >
            <template #head()="data">
              {{ data.label.toUpperCase() }}
            </template>
            <template #cell(status)="data">
              {{ statusValues[data.item.status].status }}
            </template>
            <template #cell(cityCounty)="data">
              <template v-if='data.item.City'>
                {{ data.item.City }} /
              </template>
              {{ data.item.County }}
            </template>
            <template #cell(actions)="data">
              <div class="row">
                <div class="col">
                  <a target="_blank" rel="noopener noreferrer"
                    :href="getDirectionsUrl(data.item.LongLat)"
                  >Get Directions</a>
                  <template  v-if="data.item.status !== 'available'">
                  |
                  </template>
                  <b-link v-if="data.item.status !== 'available'"
                    @click="deleteCommunity(data.item.CommunityId)"
                  >Remove</b-link>
                </div>
              </div>
            </template>
            <template #cell(action-buttons)="data">
              <div>
                <router-link
                  v-if="data.item.status === 'downloaded' || data.item.status === 'started'"
                  class='btn btn-primary btn-sm action-button-lg'
                  :to="{ name: 'assessment', params: { community_id: data.item.CommunityId }}"
                >
                  <span class="in-button-left">
                    <img
                      :src=
                      "require(`@/assets/${statusValues[data.item.status].buttonImageName}.png`)"
                      :alt="`${statusValues[data.item.status].buttonName}`"
                    />
                  </span>
                  <span
                    class="in-button-right"
                  >{{ statusValues[data.item.status].buttonName }}</span>
                </router-link>
                <b-button
                  v-if="data.item.status === 'finished'"
                  variant='primary'
                  size='sm'
                  class='action-button-lg'
                  @click="uploadCommunity(data.item)"
                  aria-label="Upload Community"
                >
                  <span class="in-button-left">
                    <img
                      :src=
                      "require(`@/assets/${statusValues[data.item.status].buttonImageName}.png`)"
                      :alt="`${statusValues[data.item.status].buttonName}`"
                    />
                  </span>
                  <span
                    class="in-button-right"
                  >{{ statusValues[data.item.status].buttonName }}</span>
                </b-button>
                <b-button
                  v-if="data.item.status === 'available'"
                  variant='primary'
                  size='sm'
                  class='action-button-lg'
                  @click="downloadCommunity(data.item)"
                  aria-label="Download Community"
                >
                  <span class="in-button-left">
                    <img :src=
                      "require(`@/assets/${statusValues[data.item.status].buttonImageName}.png`)"
                      :alt="`${statusValues[data.item.status].buttonName}`"
                    />
                  </span>
                  <span
                    class="in-button-right"
                    >{{ statusValues[data.item.status].buttonName }}</span>
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
        </template>
        <!-- Communities Needing Assessment for small devices -->
        <transition-group name="flip-list" tag="div" class='d-block d-lg-none'>
          <!-- Had to add 'A' to the key because of ad hoc ids being negative but this interpreting
           them as positive and thus, duplicates -->
          <div v-for='community in availableCommunities'
            :key="'S' + community.CommunityId"
            class='summary-box'>
            <b-row>
              <div class="col-1">
                <button
                  v-if="community.status !== 'available'"
                  type="button" class="close opacity-1" aria-label="Close"
                  @click="deleteCommunity(community.CommunityId)"
                ><span aria-hidden="true" class="remove-cross"
                  >&times;</span></button>
              </div>
              <div class='col-8'>
                <div class='assessment-title'>
                  <span class="lg-view">{{ community.CommunityName }}</span>
                  <span class="sm-view">Community: {{ community.CommunityName }}</span>
                </div>
                <div>
                  <span class="lg-view"
                    >{{ community.ProjectName }}, {{ community.County }}</span>
                  <span class="sm-view"
                    >Project: {{ community.ProjectName }}, {{ community.County }}</span>
                </div>
              </div>
              <div class='col-3' align="center">
                <router-link
                  v-if="community.status === 'downloaded'"
                  :to="{ name: 'assessment', params: { community_id: community.CommunityId }}"
                  v-slot="{href}"
                >
                  <b-button variant="success" :href="href"
                    class='action-button'
                    aria-label="Start">
                    <span><img src='@/assets/start.png'/></span>
                  </b-button>
                </router-link>
                <router-link
                  v-if="community.status === 'started'"
                  :to="{ name: 'assessment', params: { community_id: community.CommunityId }}"
                  v-slot="{href}"
                >
                  <b-button variant="success" :href="href"
                    class='action-button'
                    aria-label="Continue">
                    <span><img src='@/assets/continue.png'/></span>
                  </b-button>
                </router-link>
                <b-button variant="success" class='action-button'
                  v-if="community.status === 'finished'"
                  @click="uploadCommunity(community)"
                  aria-label="Upload Community"
                >
                  <img src='@/assets/upload.png'/>
                </b-button>
                <b-button variant="success" class='action-button'
                  v-if="community.status === 'available'"
                  @click="downloadCommunity(community)"
                  aria-label="Download Community"
                >
                  <img src='@/assets/download.png'/>
                </b-button>
              </div>
            </b-row>
            <div class="row">
              <div class="col-1">
                <!-- left empty on purpose -->
              </div>
              <div class="col mt-2">
                <b-link class="green-link"
                   target="_blank" rel="noopener noreferrer"
                  :href="getDirectionsUrl(community.LongLat)"
                  >Get Directions</b-link>
              </div>
            </div>
            <b-row>
            </b-row>
          </div>
        </transition-group>
      </template>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import helpers from '@/mixins/helpers';

export default {
  name: 'Home',
  components: {
  },
  mixins: [helpers],
  data() {
    return {
      selected: [],
      allSelected: false,
      transProps: {
        // Transition name
        name: 'flip-list',
      },
      statusValues: {
        downloaded: {
          status: 'Downloaded',
          buttonName: 'Start',
          buttonImageName: 'start-in-button',
        },
        available: {
          status: 'Available for Download',
          buttonName: 'Download',
          buttonImageName: 'download-in-button',
        },
        started: {
          status: 'Assessment Started',
          buttonName: 'Continue',
          buttonImageName: 'continue-in-button',
        },
        finished: {
          status: 'Assessment Finished',
          buttonName: 'Upload',
          buttonImageName: 'upload-in-button',
        },
      },
      fields: [
        {
          key: 'status', label: 'Status',
        },
        {
          key: 'CommunityName', label: 'Community Name', tdClass: 'table-col-max-width-250', thClass: 'table-col-max-width-250',
        },
        {
          key: 'ProjectName', label: 'Project Name', tdClass: 'table-col-max-width-250', thClass: 'table-col-max-width-250',
        },
        {
          key: 'cityCounty', label: 'City/County',
        },
        {
          key: 'actions', label: 'Actions',
        },
        {
          key: 'action-buttons', label: '',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      communityAssessorUrl: (state) => state.config.communityAssessorUrl,
      communities: (state) => state.communities,
      nextAdHocCommId: (state) => state.nextAdHocCommunityId,
      loading: (state) => state.communitiesLoading,
      projects: (state) => state.projects,
      loggedIn: (state) => state.loggedIn,
      finishSuccess: (state) => state.finishSuccess,
      uploadSuccess: (state) => state.uploadSuccess,
      uploading: (state) => state.uploading,
      online: (state) => state.online,
    }),
    ...mapGetters([
      'availableCommunities',
    ]),
  },
  mounted() {
    this.getStuffFromServer();
  },
  methods: {
    getStuffFromServer() {
      this.$store.dispatch('fetchConfiguration').then(() => {
        this.$store.dispatch('refreshLoggedInStatus').then(() => {
          if (!this.isLoggedIn()) {
            this.$router.push({ name: 'LogIn' });
          }
          this.$store.dispatch('fetchCommunities');
          this.$store.dispatch('fetchProjects');
        });
        this.$store.dispatch('fetchAssessmentCategories');
        this.$store.dispatch('fetchFireDepartments');
        this.$store.dispatch('fetchAssessmentModel');
      });
    },
    resetFinishSuccess() {
      this.$store.dispatch('resetFinishSuccess');
    },
    resetUploadSuccess() {
      this.$store.dispatch('resetUploadSuccess');
    },
    getDirectionsUrl(communityLongLat) {
      let directionsUrl = '';

      if (communityLongLat) {
        const aBits = communityLongLat
          .replace('POINT (', '')
          .replace(')', '')
          .replace(' ', ', ')
          .split(', ');
        const latInDecimalDegrees = Number.parseFloat(aBits[1]).toFixed(6).toString();
        const lonInDecimalDegrees = Number.parseFloat(aBits[0]).toFixed(6).toString();

        const ua = window.navigator.userAgent;
        if (ua.match(/iPad/i) || ua.match(/iPhone/i) || ua.match(/macintosh/)) {
          // iPad or iPhone
          // directionsUrl = `maps://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${latInDecimalDegrees},${lonInDecimalDegrees}`;
          directionsUrl = `http://maps.apple.com/?daddr=${latInDecimalDegrees},${lonInDecimalDegrees}`;
        } else {
          // Anything else
          directionsUrl = `https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=${latInDecimalDegrees},${lonInDecimalDegrees}`;
        }
      }

      return directionsUrl;
    },
    async downloadCommunity(community) {
      if (!navigator.onLine) {
        this.$bvModal.msgBoxOk('You are currently offline. You must be connected to the internet to download a community.',
          {
            centered: true,
          });
      } else {
        this.$store.dispatch('refreshLoggedInStatus')
          .then(() => {
            if (this.isLoggedOutAndOnline()) {
              const msgBoxTitle = 'Please log in with valid username and password to download communities.';

              this.$bvModal.msgBoxOk(msgBoxTitle,
                {
                  centered: true,
                })
                .then(() => {
                  this.$router.push({ name: 'LogIn' });
                });
            } else {
              this.$store.dispatch('downloadCommunity', community.CommunityId);
            }
          });
      }
    },
    async showAboutModal() {
      const h = this.$createElement;
      const messageVNode = h('div',
        [h('p',
          {
            domProps: {
              innerHTML: 'The ‘Communities Needing Assessment’ page of this Field Assessment Tool <strong>must be added to your device Home Screen to access it while offline</strong>.',
            },
          }),
        h('p',
          {
            domProps: {
              innerHTML: 'The steps to Add to Home Screen vary depending on your browser and device. If unsure, please consult the instructions for your browser.',
            },
          }),
        h('p',
          {
            domProps: {
              innerHTML: 'When online you can',
            },
          }),
        h('ul',
          {
            domProps: {
              innerHTML: '<li>Download communities</li><li>Upload assessments</li><li>Enter assessment details</li>',
            },
          }),
        h('p',
          {
            domProps: {
              innerHTML: 'When offline you can',
            },
          }),
        h('ul',
          {
            domProps: {
              innerHTML: '<li>Enter assessment details for downloaded communities</li>',
            },
          }),
        ]);

      this.$bvModal.msgBoxOk([messageVNode],
        {
          title: 'About This Tool',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'Okay',
          cancelTitle: 'No',
          cancelVariant: 'danger',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
    },
    async deleteCommunity(communityId) {
      this.$bvModal.msgBoxConfirm('Any associated assessment details you have collected here will also be permanently deleted.',
        {
          title: 'Are you sure you want to remove this community from your list?',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'Yes - Remove Community and Assessment',
          cancelTitle: 'No',
          cancelVariant: 'danger',
          footerClass: 'p-2',
          hideHeaderClose: true,
          centered: true,
        })
        .then((userClickedYes) => {
          if (userClickedYes) {
            // TODO maybe: delete possible associated pictures from db:
            // access community data from indexeddb, read AssessmentPictureIds
            // and send calls to delete pictures (make new endpoint that only takes photo id)
            this.$store.dispatch('deleteDownloadedCommunity', communityId);
            // if it's an ad hoc community, set the next ad hoc id
            if (communityId < 0) {
              this.$store.dispatch('setNextAdHocCommunityId');
            }
            this.$store.dispatch('fetchCommunities');
            this.$store.dispatch('fetchProjects');
          }
        });
    },
    async uploadCommunity(community) {
      if (!navigator.onLine) {
        this.$bvModal.msgBoxOk('You are currently offline. Please connect to the internet before uploading assessments.', {
          centered: true,
        });
      } else if (this.isLoggedOutAndOnline()) {
        const msgBoxTitle = 'Please log in with valid username and password before uploading assessments. '
        + 'Upon acceptance of this message you will be redirected to the Sign In page.';

        this.$bvModal.msgBoxOk(msgBoxTitle,
          {
            centered: true,
          })
          .then(() => {
            this.$router.push({ name: 'LogIn' });
          });
      } else {
        this.$bvModal.msgBoxConfirm('Are you sure you want to upload this assessment?',
          {
            id: 'confirm-upload',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: 'Yes',
            cancelTitle: 'No',
            cancelVariant: 'danger',
            footerClass: 'p-2',
            hideHeaderClose: true,
            centered: true,
          })
          .then((userClickedYes) => {
            if (userClickedYes) {
              this.$store.dispatch('setUploading', true);
              // first, upload photos if there are any, and update the AssessmentPictureIds
              if (community.Image1File
                || (community.Image1File && community.Image2File)
                || (community.Image1File && community.Image2File
                  && community.Image3File)) {
                this.$store.dispatch('uploadPhotos', community)
                  .then(() => this.dispatchUploadCommunity(community)
                    .finally(() => this.$store.dispatch('setUploading', false)));
              } else {
                this.dispatchUploadCommunity(community)
                  .finally(() => this.$store.dispatch('setUploading', false));
              }
            }
          });
      }
    },
    async dispatchUploadCommunity(community) {
      this.$store.dispatch('refreshLoggedInStatus')
        .then(() => {
          if (this.isLoggedOutAndOnline()) {
            const msgBoxTitle = 'Please log in with valid username and password before uploading assessments.';

            this.$bvModal.msgBoxOk(msgBoxTitle,
              {
                centered: true,
              })
              .then(() => {
                this.$router.push({ name: 'LogIn' });
              });
          } else {
            this.$store.dispatch('uploadCommunity', community)
              .then(() => this.$store.dispatch('setUploadSuccess'))
              .catch((exc) => {
                console.log(`error: ${exc}`);
                console.log(JSON.stringify(exc));

                const h = this.$createElement;
                let messageVNode = 'Upload Failed';

                // Below values must be kept in sync with what the Community Assessor returns
                const backendErrMsgWhenProjectNotOpen = 'This operation is not allowed because the Project is not Open';
                const backendErrMsgWhenUserNotAuthorized = 'You are not authorized to view the page you are trying to access';
                const backendErrMsgWhenValidationFailed = 'Validation Failed';
                const backendErrMsgWhenAlreadyAssessed = 'has already been assessed';
                const backendErrMsgWhenCommunityNameExists = 'Community Name must be unique';

                if (exc.statusText.includes(backendErrMsgWhenProjectNotOpen)
                  || (exc.data && typeof (exc.data) === 'string'
                      && exc.data.includes(backendErrMsgWhenProjectNotOpen))) {
                  messageVNode = h('div', [
                    h('p', { class: ['text-left'] }, ['This Project is no longer accepting assessments.']),
                    h('p', { class: ['text-left'] }, ['You can delete this community and its assessment from your list, or cancel this upload '
                    + 'to confirm with the Project Manager before trying to upload again.']),
                  ]);
                } else if (exc.statusText.includes(backendErrMsgWhenUserNotAuthorized)
                  || (exc.data && typeof (exc.data) === 'string'
                      && exc.data.includes(backendErrMsgWhenUserNotAuthorized))) {
                  messageVNode = h('div', [
                    h('p', { class: ['text-left'] }, ['You are no longer listed as an authorized team member for the associated Project.']),
                    h('p', { class: ['text-left'] }, ['You can delete this community and its assessment from your list, or cancel this upload '
                    + 'to confirm with the Project Manager before trying to upload again.']),
                  ]);
                } else if (exc.statusText.includes(backendErrMsgWhenValidationFailed)
                  || exc.status === 400
                  || (exc.data && typeof (exc.data) === 'string'
                      && exc.data.includes(backendErrMsgWhenValidationFailed))) {
                  let mainErrorMsg = backendErrMsgWhenValidationFailed;
                  if (exc.data.ServerErrors
                    && exc.data.ServerErrors[0].includes(backendErrMsgWhenAlreadyAssessed)) {
                    mainErrorMsg = 'Upload cannot be performed because this community has already been assessed. Please remove the community from your list.';
                  } else if (exc.data.ServerErrors
                    && exc.data.ServerErrors[0].includes(backendErrMsgWhenCommunityNameExists)) {
                    mainErrorMsg = 'The name entered for this ad hoc community is already in use for this Project,'
                    + ' and cannot be uploaded. Please change the name of the community already associated to the'
                    + ' Project in the Community Assessor Tool before uploading this ad hoc community. (Any '
                    + 'reconciliation between the names can then be done on the web, post upload.)';
                  } else {
                    const uniqueErrors = [...new Set(exc.data.ServerErrors)];
                    if (uniqueErrors.length > 1) {
                      mainErrorMsg = uniqueErrors.join(', ');
                    } else {
                      /* eslint-disable prefer-destructuring */
                      mainErrorMsg = uniqueErrors[0];
                      /* eslint-enable prefer-destructuring */
                    }
                  }

                  messageVNode = h('div', [
                    h('p', { class: ['text-left'] }, [mainErrorMsg]),
                  ]);
                }

                this.$bvModal.msgBoxConfirm([messageVNode],
                  {
                    title: 'Upload Failed',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Delete Assessment from My Device',
                    cancelVariant: 'success',
                    cancelTitle: 'Cancel Upload',
                    hideHeaderClose: true,
                    centered: true,
                  })
                  .then((userClickedDelete) => {
                    if (userClickedDelete) {
                      this.$store.dispatch('deleteDownloadedCommunity', community.CommunityId);
                      this.$store.dispatch('fetchCommunities');
                      this.$store.dispatch('fetchProjects');
                    }
                  });
              });
          }
        });
    },
    showTextWhenNoAvailableCommunities() {
      if (navigator.onLine) {
        return 'No communities are currently available for you to assess.';
      }
      return 'No communities have been downloaded for offline assessment. '
      + 'Please connect to the internet to download available communities for assessment.';
    },
    createAdHocCommunity() {
      if (this.projects.length === 0) {
        const noAuthProjsMsg = 'You cannot perform an ad hoc assessment of an unplanned community '
        + 'because you are not currently a member of an open Project. Check with the Assessments Manager '
        + 'to ensure you have the appropriate membership.';
        this.showOkModal('Project Membership Not Found', noAuthProjsMsg);
      } else {
        this.$router.push({ name: 'AdHocCommunity' });
      }
    },
  },
};
</script>
